"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Count-up number used in the About "By the numbers" stats cards.
 *
 * - On viewport entry, counts from 0 to `target` over 1.5s using ease-out cubic.
 * - Honours a per-card `delayMs` to stagger animations (150ms between cards).
 * - Uses IntersectionObserver — won't animate if section is below the fold.
 * - Animates once per page visit, never repeats.
 * - If `target` is null, renders the placeholder `—` and skips the animation.
 */
export function StatNumber({
  target,
  delayMs = 0,
  suffix = "+",
  durationMs = 1500,
}: {
  target: number | null;
  delayMs?: number;
  suffix?: string;
  durationMs?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (target === null || target === undefined) return;
    const el = ref.current;
    if (!el) return;

    let raf: number | undefined;
    let startTimer: ReturnType<typeof setTimeout> | undefined;
    let started = false;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reducedMotion) {
      setValue(target);
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          started = true;
          startTimer = setTimeout(() => {
            const startTime = performance.now();
            const step = (now: number) => {
              const t = Math.min(1, (now - startTime) / durationMs);
              const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
              setValue(Math.round(eased * target));
              if (t < 1) {
                raf = requestAnimationFrame(step);
              }
            };
            raf = requestAnimationFrame(step);
          }, delayMs);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    io.observe(el);

    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
      if (startTimer) clearTimeout(startTimer);
    };
  }, [target, delayMs, durationMs]);

  if (target === null || target === undefined) {
    return <span ref={ref}>—</span>;
  }

  return (
    <span ref={ref}>
      {value}
      {suffix}
    </span>
  );
}
